import { RefreshCw } from "lucide-react";

import { Alert, AlertDescription } from "@/core/components/ui/alert";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import { Card, CardContent, CardHeader, CardTitle } from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import { AmountMinor } from "@/features/shared";
import { useEmoneyReconciliation } from "@/features/wallet/api/use-emoney-reconciliation";

/**
 * Rapprochement quotidien circulation ↔ net émis. `differenceMinor` non
 * nul → alerte (clôture du jour à instruire).
 */
export function EmoneyReconciliationCard({
  currency,
  t,
}: {
  currency: string;
  t: (key: string) => string;
}) {
  const { data, isLoading, isFetching, error, refetch } =
    useEmoneyReconciliation(currency);
  const difference = data?.differenceMinor ?? 0;
  const unbalanced = difference !== 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">
          {t("wallet.emoney.reconciliation.title")}
        </CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? <ButtonSpinner /> : <RefreshCw className="size-4" />}
          {t("wallet.emoney.reconciliation.refresh")}
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error.message}</p>
        ) : data ? (
          <>
            {unbalanced ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {t("wallet.emoney.reconciliation.alert")}
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-success">
                {t("wallet.emoney.reconciliation.ok")}
              </p>
            )}
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-sm text-muted-foreground">
                  {t("wallet.emoney.reconciliation.outstanding")}
                </dt>
                <dd className="font-medium">
                  <AmountMinor
                    amountMinor={data.outstandingEmoneyMinor}
                    currency={data.currency}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  {t("wallet.emoney.reconciliation.netIssued")}
                </dt>
                <dd className="font-medium">
                  <AmountMinor
                    amountMinor={data.netIssuedMinor}
                    currency={data.currency}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  {t("wallet.emoney.reconciliation.difference")}
                </dt>
                <dd
                  className={
                    unbalanced ? "font-medium text-destructive" : "font-medium"
                  }
                >
                  <AmountMinor
                    amountMinor={data.differenceMinor}
                    currency={data.currency}
                  />
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">
                  {t("wallet.emoney.reconciliation.generatedAt")}
                </dt>
                <dd className="font-medium">
                  <NoTranslate as="span">
                    {data.generatedAt
                      ? formatDateWithFallback(data.generatedAt, "—")
                      : "—"}
                  </NoTranslate>
                </dd>
              </div>
            </dl>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
